import Skeleton from "react-loading-skeleton";
import styled, { useTheme } from "styled-components";
import { Row, TableCustom, Tr } from "../table/styles";

type TableSkeletonProps = {
  rows: number;
  columns: number;
  columnsData?: any[];
};

const DesktopSkeleton = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Thead = styled.thead`
  height: 44.5px;
  background-color: ${({theme}) => theme.palette[200]};
  border-collapse: initial;
`;

const StyledSkeleton = styled(Skeleton)`
  background-color: ${({theme}) => theme.palette[200]};
`;

const CardSkeletonGrid = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
    width: 100%;
    margin-top: 15px;
  }
`;

const CardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: ${({theme}) => theme.radius.control};
  background: ${({theme}) => theme.base.white};
  box-shadow: ${({theme}) => `inset 0 0 0 1px ${theme.grey[350]}, ${theme.shadows.control}`};
`;

const SkeletonHeader = styled.div`
  display: grid;
  grid-template-columns: 16px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 10px;
`;

const SkeletonFields = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  padding-top: 12px;
  border-top: ${({theme}) => `1px solid ${theme.grey[300]}`};

  @media (max-width: 360px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const TableSkeleton = ({ rows, columns }: TableSkeletonProps) => {
  const theme: any = useTheme();
  const safeColumnCount = Math.max(columns, 1);
  const columnWidth = `${100 / safeColumnCount}%`;

  return (
    <>
      <DesktopSkeleton>
        <TableCustom style={{ marginTop: "15px" }}>
          <Thead>
            <Tr>
              {Array.from({ length: safeColumnCount }, (_, index) => <th key={index} />)}
            </Tr>
          </Thead>
          <tbody>
            {Array.from({ length: rows }, (_, rowIndex) => (
              <tr key={rowIndex}>
                {Array.from({ length: safeColumnCount }, (_, columnIndex) => (
                  <Row key={columnIndex} style={{ height: "41px", width: columnWidth }}>
                    <StyledSkeleton baseColor={theme.palette[300]} highlightColor={theme.palette[20]} />
                  </Row>
                ))}
              </tr>
            ))}
          </tbody>
        </TableCustom>
      </DesktopSkeleton>
      <CardSkeletonGrid aria-label="Loading table cards">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <CardSkeleton key={rowIndex}>
            <SkeletonHeader>
              <StyledSkeleton height={16} width={16} baseColor={theme.palette[300]} highlightColor={theme.palette[20]} />
              <StyledSkeleton height={20} baseColor={theme.palette[300]} highlightColor={theme.palette[20]} />
              <StyledSkeleton height={44} width={44} baseColor={theme.palette[300]} highlightColor={theme.palette[20]} />
            </SkeletonHeader>
            <SkeletonFields>
              {Array.from({ length: Math.min(Math.max(safeColumnCount - 2, 2), 4) }, (_, fieldIndex) => (
                <StyledSkeleton key={fieldIndex} height={32} baseColor={theme.palette[300]} highlightColor={theme.palette[20]} />
              ))}
            </SkeletonFields>
          </CardSkeleton>
        ))}
      </CardSkeletonGrid>
    </>
  );
};

export default TableSkeleton;
